import React from "react";
import { Link } from "react-router-dom";
import LegalPageLayout from "@/components/site/LegalPageLayout";
import { LEGAL_ENTITY, HOSTING_INFO, CRM_TOOL } from "@/data/legal";

const LAST_UPDATED = "11 de setembro de 2026";

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Política de Privacidade"
      description={`Como a ${LEGAL_ENTITY.tradeName} coleta, usa e protege seus dados pessoais, em conformidade com a LGPD.`}
      path="/politica-de-privacidade"
      lastUpdated={LAST_UPDATED}
    >
      <div className="mn-legal-notice">
        <strong>Aviso:</strong> este é um modelo de referência, baseado em modelos comuns de
        mercado — não foi validado por um advogado. Passe por revisão jurídica antes de considerar
        esta página definitiva, principalmente a base legal de cada tratamento e a seção de
        transferência internacional de dados.
      </div>

      <h2>1. Quem é o controlador dos seus dados</h2>
      <p>
        Esta Política de Privacidade é da <strong>{LEGAL_ENTITY.legalName}</strong> (nome fantasia{" "}
        <strong>{LEGAL_ENTITY.tradeName}</strong>), CNPJ <strong>{LEGAL_ENTITY.cnpj}</strong>, com
        sede em {LEGAL_ENTITY.address}, doravante "nós" ou "{LEGAL_ENTITY.tradeName}". Somos o
        controlador dos dados pessoais tratados através deste site, nos termos da Lei nº
        13.709/2018 (Lei Geral de Proteção de Dados — LGPD).
      </p>
      <p>
        Não temos um encarregado de dados (DPO) com contato dedicado — as solicitações sobre seus
        dados devem ser feitas pelo e-mail{" "}
        <a href={`mailto:${LEGAL_ENTITY.dpoContact}`}>{LEGAL_ENTITY.dpoContact}</a>.
      </p>

      <h2>2. Quais dados coletamos e por quê</h2>
      <h3>2.1 Formulário de orçamento</h3>
      <p>
        Ao solicitar um orçamento (seja pelo botão "Solicitar Orçamento" ou pelo formulário na
        seção de contato), coletamos: nome, e-mail, telefone/WhatsApp, empresa (opcional), produto
        de interesse, quantidade e uma mensagem opcional. Usamos esses dados para preparar e
        enviar sua proposta comercial e para dar continuidade ao atendimento — inclusive, se você
        optar por isso, abrindo uma conversa pré-preenchida no seu próprio WhatsApp.
      </p>
      <p>
        Também registramos, junto com o orçamento, os parâmetros de UTM presentes no link que você
        usou pra chegar ao site (ex.: <code>utm_source</code>, <code>utm_campaign</code>), quando
        existirem — servem só para sabermos de qual anúncio ou campanha o seu contato veio, não
        identificam você sozinhos.
      </p>
      <h3>2.2 Formulário de contato e newsletter</h3>
      <p>
        O site também tem uma estrutura de formulário de contato geral (nome, e-mail, telefone
        opcional e mensagem) e de inscrição em newsletter (e-mail), com a mesma finalidade — dar
        andamento ao seu contato conosco ou enviar comunicações, quando você optar por se
        inscrever. Na data desta política, esses dois formulários não estão visíveis no site (só o
        de orçamento está em uso), mas os dados seriam tratados da mesma forma caso sejam
        ativados.
      </p>
      <h3>2.3 Ferramenta de mock-up (upload de arte)</h3>
      <p>
        Ao enviar uma imagem/logotipo na ferramenta de geração automática de mock-up, o arquivo é
        processado apenas em memória para compor o preview e <strong>não é armazenado</strong> em
        nenhum banco de dados ou disco — é descartado assim que a resposta é enviada ao seu
        navegador.
      </p>
      <h3>2.4 Navegação no site</h3>
      <p>
        Se você consentir com cookies de analíticos e marketing (ver nossa{" "}
        <Link to="/politica-de-cookies">Política de Cookies</Link>), coletamos dados de navegação
        (páginas visitadas, origem do acesso) através do Google Analytics, Google Tag Manager e
        Meta Pixel.
      </p>

      <h2>3. Base legal</h2>
      <ul>
        <li>
          <strong>Execução de procedimentos preliminares a contrato</strong> (Art. 7º, V) — dados
          do formulário de orçamento, necessários para elaborar e enviar a proposta que você
          solicitou.
        </li>
        <li>
          <strong>Consentimento</strong> (Art. 7º, I) — inscrição em newsletter e cookies de
          analíticos/marketing, que só são ativados se você autorizar no nosso banner de cookies.
        </li>
        <li>
          <strong>Legítimo interesse</strong> (Art. 7º, IX) — mensagens do formulário de contato,
          para responder a dúvidas que você mesmo nos enviou.
        </li>
      </ul>

      <h2>4. Com quem seus dados são compartilhados</h2>
      <ul>
        <li>
          <strong>Hospedagem do site e banco de dados</strong> — Hostinger, que armazena o banco
          de dados onde ficam os registros de orçamento/contato.
        </li>
        <li>
          <strong>Gestão comercial dos leads (CRM)</strong> — os dados enviados pelo formulário de
          orçamento podem ser organizados através da ferramenta <strong>{CRM_TOOL.productName}</strong>,
          operada pela {CRM_TOOL.operatorTradeName} ({CRM_TOOL.operatorLegalName}, CNPJ{" "}
          {CRM_TOOL.operatorCnpj}), agência responsável pela gestão comercial e desenvolvimento
          deste site, para fins de acompanhamento e organização do seu atendimento.
        </li>
        <li>
          <strong>Google</strong> (Analytics, Tag Manager, Search Console) e{" "}
          <strong>Meta</strong> (Meta Pixel) — apenas se você consentir com cookies de
          analíticos/marketing.
        </li>
        <li>
          <strong>WhatsApp (Meta)</strong> — quando você opta por continuar o atendimento pelo
          WhatsApp, é você mesmo quem envia a mensagem (pré-preenchida pelo site) através do seu
          próprio aplicativo, sujeito à política de privacidade do WhatsApp/Meta.
        </li>
      </ul>
      <p>Não vendemos seus dados pessoais a terceiros.</p>

      <h2>5. Transferência internacional de dados</h2>
      <p>
        O servidor que hospeda este site (Hostinger) fica em <strong>{HOSTING_INFO.country}</strong>{" "}
        ({HOSTING_INFO.region}), fora do Brasil — por isso seus dados podem ser transferidos e
        processados nesse país, nos termos do Art. 33 da LGPD. Google e Meta também operam
        infraestrutura global, o que pode implicar processamento de dados fora do Brasil quando
        você consente com cookies de analíticos/marketing.
      </p>

      <h2>6. Por quanto tempo guardamos seus dados</h2>
      <p>
        Mantemos seus dados pelo tempo necessário para cumprir as finalidades descritas nesta
        política, ou até você solicitar a exclusão (ver seção 7). <em>[TODO: definir um prazo de
        retenção específico com orientação jurídica — hoje não há um período fixo configurado no
        sistema.]</em>
      </p>

      <h2>7. Seus direitos (Art. 18 da LGPD)</h2>
      <p>Você pode, a qualquer momento, solicitar:</p>
      <ul>
        <li>Confirmação de que tratamos seus dados e acesso a eles;</li>
        <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
        <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em excesso;</li>
        <li>Portabilidade dos dados a outro fornecedor de serviço;</li>
        <li>Eliminação dos dados tratados com base no seu consentimento;</li>
        <li>Informação sobre com quem compartilhamos seus dados;</li>
        <li>Revogação do consentimento, a qualquer momento.</li>
      </ul>
      <p>
        Para exercer qualquer um desses direitos, escreva para{" "}
        <a href={`mailto:${LEGAL_ENTITY.dpoContact}`}>{LEGAL_ENTITY.dpoContact}</a>.
      </p>

      <h2>8. Alterações desta política</h2>
      <p>
        Podemos atualizar esta política periodicamente. A data da última atualização está sempre
        indicada no topo desta página.
      </p>
    </LegalPageLayout>
  );
}
